import { useState } from 'react';
import { toast } from 'sonner';
import { ArrowRightLeft, FolderInput, Trash2, UserPlus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { useBulkTaskAction } from '@/hooks/use-tasks';
import { useBoards, useBoardPermission } from '@/hooks/use-boards';
import type { BulkTaskPayload } from '@/lib/bulk-tasks';
import type { BoardListItem, ProjectMemberItem, TaskStatusItem } from '@hygge/shared';

interface BulkActionBarProps {
  boardId: string;
  statuses: TaskStatusItem[];
  members: ProjectMemberItem[];
  selectedKeys: Set<string>;
  /** Called after the selection has been acted on (or cleared). */
  onDone: () => void;
  /** Called after an action succeeded — the board leaves select mode then. */
  onSucceeded?: () => void;
}

export function BulkActionBar({
  boardId,
  statuses,
  members,
  selectedKeys,
  onDone,
  onSucceeded,
}: BulkActionBarProps) {
  const formatName = useEmployeeNameFormatter();
  const bulk = useBulkTaskAction(boardId);
  // Delete is decided per task — own or anybody's — so it is offered when this
  // board grants either, and the server reports the tasks it refused. A role
  // with neither would see every task come back failed.
  const canDeleteOwn = useBoardPermission(boardId, 'task.delete.own');
  const canDeleteAny = useBoardPermission(boardId, 'task.delete.any');
  const canDelete = canDeleteOwn || canDeleteAny;
  const { data: boards } = useBoards();
  // Confirmed separately from selection: picking a board opens the drop-warning dialog.
  const [pendingBoard, setPendingBoard] = useState<BoardListItem | null>(null);
  const count = selectedKeys.size;

  if (count === 0) return null;

  // Move-to-board targets exclude the current board (a same-board move is a no-op / rejected).
  const otherBoards = (boards ?? []).filter((b) => b.id !== boardId);

  const run = (payload: Omit<BulkTaskPayload, 'taskKeys'>, verb = 'updated') => {
    bulk.mutate(
      { taskKeys: [...selectedKeys], ...payload },
      {
        onSuccess: (res) => {
          toast.success(
            res.failed.length
              ? `${res.affected} tasks ${verb}, ${res.failed.length} failed`
              : `${res.affected} tasks ${verb}`,
          );
          onDone();
          // Leaves select mode only when something was done; after an all-refused
          // action the ticks are cleared but select mode stays on.
          if (res.affected > 0) onSucceeded?.();
        },
        onError: () => toast.error('Bulk action failed'),
      },
    );
  };

  const sortedStatuses = [...statuses].sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <div className="fixed bottom-6 left-1/2 z-50 flex -translate-x-1/2 items-center gap-1.5 rounded-full border border-border bg-background/95 px-3 py-2 shadow-lg backdrop-blur">
      <span className="px-2 text-sm font-medium tabular-nums">{count} selected</span>
      <div className="mx-0.5 h-5 w-px bg-border" aria-hidden />

      {/* Move to status */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 gap-1.5 rounded-full cursor-pointer"
            disabled={bulk.isPending}
          >
            <ArrowRightLeft className="h-4 w-4" />
            Move
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" className="w-52">
          <DropdownMenuLabel>Move to status</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {sortedStatuses.map((status) => (
            <DropdownMenuItem
              key={status.id}
              className="cursor-pointer"
              onSelect={() => run({ action: 'move', statusId: status.id })}
            >
              <span
                className="mr-2 h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: status.color }}
              />
              <span className="truncate">{status.name}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Assign */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 gap-1.5 rounded-full cursor-pointer"
            disabled={bulk.isPending}
          >
            <UserPlus className="h-4 w-4" />
            Assign
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" className="w-60">
          <DropdownMenuLabel className="flex flex-col gap-0.5">
            Assign to
            <span className="text-[11px] font-normal text-muted-foreground">
              Adds to the current assignees
            </span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="cursor-pointer"
            onSelect={() => run({ action: 'assign', assigneeId: null })}
          >
            <span className="mr-2 h-5 w-5 shrink-0 rounded-full border border-dashed border-border" />
            Unassigned
          </DropdownMenuItem>
          <div className="max-h-64 overflow-y-auto">
            {members.map((m) => (
              <DropdownMenuItem
                key={m.employeeId}
                className="cursor-pointer"
                onSelect={() => run({ action: 'assign', assigneeId: m.employeeId })}
              >
                <Avatar className="mr-2 h-5 w-5">
                  <AvatarImage src={m.avatarUrl || undefined} />
                  <AvatarFallback className="text-[8px]">
                    {m.firstName[0]}
                    {m.lastName[0]}
                  </AvatarFallback>
                </Avatar>
                <span className="truncate">
                  {formatName({ firstName: m.firstName, lastName: m.lastName })}
                </span>
              </DropdownMenuItem>
            ))}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Move to board */}
      {otherBoards.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 gap-1.5 rounded-full cursor-pointer"
              disabled={bulk.isPending}
            >
              <FolderInput className="h-4 w-4" />
              Move to board
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="center" className="w-56">
            <DropdownMenuLabel>Move to board</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <div className="max-h-64 overflow-y-auto">
              {otherBoards.map((b) => (
                <DropdownMenuItem
                  key={b.id}
                  className="cursor-pointer"
                  onSelect={() => setPendingBoard(b)}
                >
                  <span
                    className="mr-2 h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: b.color }}
                  />
                  <span className="truncate">{b.name}</span>
                </DropdownMenuItem>
              ))}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Move-to-board confirm — a move re-keys each task and drops board-scoped state */}
      <AlertDialog
        open={pendingBoard !== null}
        onOpenChange={(open) => {
          if (!open) setPendingBoard(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Move {count} task{count !== 1 ? 's' : ''} to {pendingBoard?.name}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Each task gets a new key on {pendingBoard?.name}. Its sprint, tags and parent link are
              dropped (all board-specific); assignees, labels, comments and activity history are
              kept.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="cursor-pointer">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="cursor-pointer"
              onClick={() => {
                if (pendingBoard) {
                  run({ action: 'move_board', targetBoardId: pendingBoard.id }, 'moved');
                }
                setPendingBoard(null);
              }}
            >
              Move
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete */}
      {canDelete && (
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 gap-1.5 rounded-full text-red-600 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950/40 cursor-pointer"
            disabled={bulk.isPending}
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {count} task{count !== 1 ? 's' : ''}?</AlertDialogTitle>
            <AlertDialogDescription>
              This soft-deletes the selected task{count !== 1 ? 's' : ''}. They will be removed from
              the board but their links and activity history are preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="cursor-pointer">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 cursor-pointer"
              onClick={() => run({ action: 'delete' })}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      )}

      <div className="mx-0.5 h-5 w-px bg-border" aria-hidden />
      <Button
        variant="ghost"
        size="sm"
        className="h-8 gap-1.5 rounded-full text-muted-foreground hover:text-foreground cursor-pointer"
        onClick={onDone}
      >
        <X className="h-4 w-4" />
        Clear
      </Button>
    </div>
  );
}
